import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import GeneralLoaderWrapper from '@common/components/General/GeneralLoaderWrapper';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import {
  DataGrid,
  GridColDef,
  gridClasses,
  DataGridProps,
  GridRenderCellParams,
  GridLocaleText,
} from '@mui/x-data-grid';
import { enUS, deDE, itIT, esES, jaJP, frFR, plPL } from '@mui/x-data-grid/locales';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import User from '../../models/general/user';
import ListOrganization from '../../models/organization/list-organization';
import Robot from '../../models/robots/robot';
import MissionList from '../../models/missions/mission-list';
import ListAgreement from '../../models/general/agreements/list-agreement';
import Acceptance from '../../models/general/agreements/acceptance';
import CustomLocalization from '../../models/general/custom-localizations/custom-localization';
import DeploymentWeeks from '../../models/organization/deployment-week';
import Deployment from '../../models/organization/deployment';
import NotificationSubscription from '../../models/general/notifications/notification-trigger';
import {
  ConnectedIcon,
  DeploymentEndedIcon,
  FailedIcon,
  SuccessIcon,
  WebsocketDisconnectedIcon,
} from '@common/utils/icons/icons';
import UpdateVersion from '../../models/general/versions/update-version';
import i18n from '../../i18n';
import RobotContact from '../../models/robots/robot-contact';
import CustomContextMenu from './CustomContextMenu';
import { ContextMenuOption, ContextMenuState } from '../../models/misc/context-menu';
import { RobotHealthTable } from 'models/fleet-management/FleetWidget';
import {
  FleetDataHealthTransition,
  FleetDataPerformanceChange,
} from 'models/fleet-management/FleetDataSet';

interface CustomTableProps extends DataGridProps {
  getStatus: HttpFetchStatus;
  loadingMessage?: string;
  dataLoaded: boolean;
  failedToLoadMessage: string;
  emptyDataMessage: string;
  data:
    | ListOrganization[]
    | User[]
    | Robot[]
    | MissionList[]
    | ListAgreement[]
    | Acceptance[]
    | CustomLocalization[]
    | DeploymentWeeks[]
    | NotificationSubscription[]
    | UpdateVersion[]
    | Deployment[]
    | RobotContact[]
    | RobotHealthTable[]
    | FleetDataHealthTransition[]
    | FleetDataPerformanceChange[];
  columns: GridColDef[];
  contextMenuOptions?: ContextMenuOption[];
  rowClickHandler?: (id: string) => void;
  customLabelRowsPerPage?: string;
  page?: number;
  pageSize?: number;
  onPageChange?: (newPage: number, newSize: number) => void;
  rowCount?: number;
}

export const renderBooleanCell = (params: GridRenderCellParams) => (
  <Box
    sx={{
      height: '100%',
      alignItems: 'center',
      display: 'flex',
    }}
  >
    {params.value === true ? <SuccessIcon color="success" /> : <FailedIcon color="error" />}
  </Box>
);

export const renderConnectionCell = (params: GridRenderCellParams) => (
  <Box
    sx={{
      height: '100%',
      alignItems: 'center',
      display: 'flex',
    }}
  >
    {params.value == null ? (
      <DeploymentEndedIcon color="action" />
    ) : params.value ? (
      <ConnectedIcon color="success" />
    ) : (
      <WebsocketDisconnectedIcon color="warning" />
    )}
  </Box>
);

const CustomTable: React.FC<CustomTableProps> = ({
  getStatus,
  loadingMessage,
  dataLoaded,
  failedToLoadMessage,
  emptyDataMessage,
  data,
  checkboxSelection = false,
  columns,
  hideFooter,
  autoHeight,
  contextMenuOptions,
  rowClickHandler,
  customLabelRowsPerPage,
  page,
  pageSize,
  onPageChange,
  rowCount,
  ...props
}) => {
  let locale = enUS;
  if (i18n.language === 'de' || i18n.language === 'ch') {
    locale = deDE;
  } else if (i18n.language === 'it') {
    locale = itIT;
  } else if (i18n.language === 'es') {
    locale = esES;
  } else if (i18n.language === 'ja') {
    locale = jaJP;
  } else if (i18n.language === 'fr') {
    locale = frFR;
  } else if (i18n.language === 'pl') {
    locale = plPL;
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const [formattedColumns, setFormattedColumns] = useState<GridColDef[]>(columns);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  useEffect(() => {
    const containerWidth = containerRef.current?.offsetWidth ?? 0;
    const totalTableWidth = columns.reduce((sum, col) => sum + (col.width ?? 100), 0);

    if (totalTableWidth < containerWidth) {
      setFormattedColumns(columns.map((c) => ({ ...c, width: undefined, flex: c.flex ?? 1 })));
    } else {
      setFormattedColumns(
        columns.map((c, index) =>
          index === 0
            ? {
                ...c,
                headerClassName: 'sticky-column-header',
                cellClassName: 'sticky-column-cell',
                flex: undefined,
                width: c.width ?? 150,
              }
            : { ...c, flex: undefined, width: c.width ?? 150 },
        ),
      );
    }
  }, [columns]);

  const openContextMenuHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    const rowId = event.currentTarget.getAttribute('data-id');
    if (rowId != null) {
      setContextMenu(
        contextMenu === null ? { id: rowId, mouseX: event.clientX, mouseY: event.clientY } : null,
      );
    }
  };

  const closeContextMenuHandler = () => {
    setContextMenu(null);
  };

  const customLocaleText: Partial<GridLocaleText> = {
    MuiTablePagination: {
      labelRowsPerPage:
        customLabelRowsPerPage ??
        locale.components.MuiDataGrid.defaultProps.localeText.MuiTablePagination?.labelRowsPerPage,
    },
  };

  const localeText = {
    ...locale.components.MuiDataGrid.defaultProps.localeText,
    ...(customLabelRowsPerPage ? customLocaleText : {}),
  };

  const dataWithId = useMemo(() => {
    if (Object.keys(data.length > 0 ? data[0] : []).some((f) => f === 'id')) return data;
    return data.map((d, index) => ({ ...d, id: index }));
  }, [data]);

  return (
    <React.Fragment>
      <GeneralLoaderWrapper
        getStatus={getStatus}
        loadingMessage={loadingMessage ?? ''}
        dataLoaded={dataLoaded}
        failedToLoadMessage={failedToLoadMessage}
      >
        <Box
          ref={containerRef}
          sx={{
            height: autoHeight ? undefined : '100%',
            overflow: autoHeight ? undefined : 'hidden',
            '& .sticky-column-header': {
              position: 'sticky!important',
              left: 0,
              backgroundColor: '#F2F4F7',
              zIndex: 4,
            },
            '& .sticky-column-cell': {
              position: 'sticky',
              left: 0,
              zIndex: 1,
              backgroundColor: 'inherit',
            },
          }}
        >
          {data.length === 0 ? (
            <CustomAlert header={emptyDataMessage} severity="info" />
          ) : (
            <DataGrid
              localeText={localeText}
              disableRowSelectionOnClick
              rows={dataWithId}
              {...props}
              slotProps={
                contextMenuOptions == null
                  ? undefined
                  : {
                      row: {
                        onContextMenu: openContextMenuHandler,
                        style: { cursor: 'context-menu' },
                      },
                    }
              }
              hideFooter={data.length > 100 ? false : hideFooter}
              sx={{
                '&, [class^=MuiDataGrid]': {
                  border: 'none',
                  '--DataGrid-rowBorderColor': 'transparent',
                },
                [`& .${gridClasses.columnHeader}`]: {
                  outline: 'transparent',
                },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: 'none',
                  },
                [`& .${gridClasses.cell}`]: {
                  cursor: rowClickHandler == null ? undefined : 'pointer',
                  backgroundColor: rowClickHandler == null ? 'white' : undefined,
                },
                [`& .${gridClasses.row}`]: {
                  cursor: rowClickHandler == null ? undefined : 'pointer',
                  backgroundColor: 'white',
                },

                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                  width: '0.4em',
                },
                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                },
                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
              columns={formattedColumns}
              initialState={{
                pagination: hideFooter
                  ? undefined
                  : {
                      paginationModel: { page: 0, pageSize: 20 },
                    },
              }}
              onRowClick={
                rowClickHandler == null
                  ? undefined
                  : (params) => rowClickHandler(params.id as string)
              }
              pageSizeOptions={[20, 50, 100]}
              {...(props.pagination && page != null && pageSize != null
                ? {
                    pagination: true,
                    paginationMode: 'server',
                    paginationModel: { page, pageSize },
                    onPaginationModelChange: (model) => {
                      if (onPageChange) {
                        onPageChange(model.page, model.pageSize);
                      }
                    },
                    rowCount,
                  }
                : {})}
              checkboxSelection={checkboxSelection}
              autoHeight={autoHeight}
              autosizeOptions={{
                includeHeaders: true,
                includeOutliers: true,
                expand: true,
              }}
            />
          )}
        </Box>
      </GeneralLoaderWrapper>
      {contextMenuOptions != null && (
        <CustomContextMenu
          contextMenuOptions={contextMenuOptions}
          contextMenuState={contextMenu}
          onClose={closeContextMenuHandler}
        />
      )}
    </React.Fragment>
  );
};

export default CustomTable;
